import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, Button, Link, } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { alpha } from '@mui/material/styles';
import { translateLandingAgencyDisplayName } from '../../locale/gridTranslations';
import { Link as RouterLink } from 'react-router-dom';
import { isInternalAppPath } from '../../utils/internalPath';
import { resolvePublicUrl } from '../../utils/publicUrl';
import { resolveDepartmentForAgency } from '../../services/landingDepartmentMatch';
/**
 * Open permits page with optional `?agency=` = agency record id (matches accordion `agency.id`), not display name.
 */
export function buildDepartmentAgencyHref(a) {
    const dept = (a.departmentKey ?? '').trim();
    const agencyRecord = (a.recordId ?? '').trim();
    if (!dept || !agencyRecord)
        return null;
    const qs = new URLSearchParams({ agency: agencyRecord });
    return `/permits/${encodeURIComponent(dept)}?${qs.toString()}`;
}
/**
 * Department permits page with no `?agency=` filter — for a grouped tile, where several
 * agencies share one card and no single one of them is the right destination.
 */
export function buildDepartmentHref(a) {
    const dept = (a.departmentKey ?? '').trim();
    if (!dept)
        return null;
    return `/permits/${encodeURIComponent(dept)}`;
}
/** Strip redundant “department 4, 1 …” fragments when we show a linked bureau row separately. */
function tidyAgencyDescription(text) {
    const t = text.trim();
    if (!t)
        return '';
    return t
        .replace(/\bdepartment[s]?\s*[-–—:,.]?\s*[\d,\s]+/gi, '')
        .replace(/\(\s*department[s]?\s*[-–—:,.]?\s*[\d,\s]+\s*\)/gi, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
}
/** Keeps rich hover panels in view (carousel tooltips + grouped list). */
export function agencyDetailTooltipPopperProps(theme, opts) {
    const { zIndexBoost = 0, flipFallbackPlacements, popperOffset = [0, 12] } = opts;
    return {
        style: { zIndex: theme.zIndex.tooltip + zIndexBoost },
        disablePortal: false,
        modifiers: [
            {
                name: 'offset',
                enabled: true,
                options: { offset: popperOffset },
            },
            {
                name: 'preventOverflow',
                enabled: true,
                options: {
                    boundary: 'viewport',
                    rootBoundary: 'viewport',
                    altAxis: true,
                    padding: 10,
                },
            },
            {
                name: 'flip',
                enabled: true,
                options: {
                    boundary: 'viewport',
                    rootBoundary: 'viewport',
                    fallbackPlacements: flipFallbackPlacements,
                },
            },
        ],
        popperOptions: { strategy: 'fixed' },
    };
}
export const AGENCY_TOOLTIP_FLIP_FROM_TOP = [
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
];
export const AGENCY_GROUP_PANEL_FLIP_PLACEMENTS = [
    'bottom',
    'bottom-end',
    'top-start',
    'top',
    'top-end',
];
/** Shared hover detail card for one grid row (carousel tooltip or group list row). */
export function AgencyDetailTooltipPaper({ agency, locale, shell, theme, resolvedDepartments, tooltipMaxW, }) {
    const displayName = translateLandingAgencyDisplayName(agency, locale);
    const subtitleLine = (agency.subtitle ?? '').trim();
    const descriptionLine = (agency.description ?? '').trim();
    const matchedDept = resolveDepartmentForAgency(agency, resolvedDepartments);
    const tidyDesc = tidyAgencyDescription(descriptionLine);
    const primaryBody = tidyDesc ||
        (!tidyDesc && descriptionLine.trim() ? descriptionLine.trim() : '') ||
        subtitleLine ||
        shell.heroAgencyHoverFallback;
    const deptThumbRaw = matchedDept ? matchedDept.logoImage || matchedDept.image || '' : '';
    const deptThumbSrc = deptThumbRaw && !/^data:/i.test(deptThumbRaw.trim())
        ? resolvePublicUrl(deptThumbRaw.trim()) ?? deptThumbRaw.trim()
        : '';
    const deptHref = matchedDept?.href?.trim();
    const deptLinkInternal = Boolean(deptHref && isInternalAppPath(deptHref));
    return (_jsx(Box, { sx: {
            position: 'relative',
            borderRadius: 2,
            overflow: 'visible',
            boxSizing: 'border-box',
            width: 'max-content',
            maxWidth: tooltipMaxW,
            bgcolor: alpha(theme.palette.common.white, 0.98),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            boxShadow: theme.shadows[8] ?? `0 10px 28px ${alpha(theme.palette.common.black, 0.14)}`,
            pointerEvents: 'none',
        }, children: _jsxs(Box, { sx: { p: 1.75 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }, children: [_jsx(Box, { sx: {
                                flexShrink: 0,
                                width: 56,
                                height: 56,
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                bgcolor: alpha(theme.palette.grey[100], 0.95),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                            }, children: deptThumbSrc ? (_jsx(Box, { component: "img", src: deptThumbSrc, alt: "", sx: {
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                } })) : (_jsx(ArticleOutlinedIcon, { sx: {
                                    fontSize: 30,
                                    color: theme.palette.primary.main,
                                    opacity: 0.85,
                                } })) }), _jsxs(Box, { sx: { minWidth: 0, flex: 1 }, children: [_jsx(Typography, { variant: "caption", component: "div", sx: {
                                        display: 'block',
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        color: alpha(theme.palette.primary.main, 0.84),
                                        mb: 0.35,
                                        lineHeight: 1.2,
                                    }, children: shell.heroAgencyTooltipEyebrow }), _jsxs(Box, { sx: {
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'baseline',
                                        gap: 0.5,
                                        columnGap: 0.75,
                                        rowGap: 0.35,
                                    }, children: [_jsx(Typography, { variant: "subtitle1", component: "span", sx: {
                                                fontWeight: 700,
                                                color: 'text.primary',
                                                letterSpacing: '-0.02em',
                                                lineHeight: 1.3,
                                                fontSize: '1.08rem',
                                            }, children: displayName }), matchedDept?.title ? (_jsxs(_Fragment, { children: [_jsx(Typography, { component: "span", variant: "subtitle1", sx: {
                                                        color: 'text.disabled',
                                                        fontWeight: 400,
                                                        lineHeight: 1.3,
                                                        userSelect: 'none',
                                                    }, "aria-hidden": true, children: "\u00B7" }), _jsx(Typography, { component: "span", variant: "caption", sx: {
                                                        color: 'text.secondary',
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem',
                                                        letterSpacing: '0.06em',
                                                        textTransform: 'uppercase',
                                                        lineHeight: 1.3,
                                                        flexShrink: 0,
                                                    }, children: shell.heroAgencyTooltipDepartmentLabel }), deptHref && deptLinkInternal ? (_jsx(Link, { component: RouterLink, to: deptHref, underline: "hover", "aria-label": `${shell.heroAgencyTooltipDepartmentLink}: ${matchedDept.title}`, sx: {
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        color: 'primary.main',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.25,
                                                        lineHeight: 1.3,
                                                        pointerEvents: 'auto',
                                                    }, children: matchedDept.title })) : deptHref ? (_jsx(Link, { href: deptHref, underline: "hover", "aria-label": `${shell.heroAgencyTooltipDepartmentLink}: ${matchedDept.title}`, sx: {
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        color: 'primary.main',
                                                        lineHeight: 1.3,
                                                        pointerEvents: 'auto',
                                                    }, children: matchedDept.title })) : (_jsx(Typography, { variant: "subtitle1", component: "span", sx: {
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        lineHeight: 1.3,
                                                        color: 'text.primary',
                                                    }, children: matchedDept.title }))] })) : null] })] })] }), _jsxs(Box, { sx: {
                        px: 1.25,
                        py: 1.1,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.grey[50], 0.85),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                    }, children: [_jsx(Typography, { variant: "body2", component: "div", sx: {
                                color: 'text.primary',
                                lineHeight: 1.65,
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                fontWeight: descriptionLine ? 500 : 400,
                                fontSize: '0.9rem',
                            }, children: primaryBody }), descriptionLine && subtitleLine && descriptionLine !== subtitleLine ? (_jsx(Typography, { variant: "caption", component: "div", sx: {
                                display: 'block',
                                mt: 1.1,
                                pt: 1.1,
                                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                color: 'text.secondary',
                                lineHeight: 1.5,
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                fontSize: '0.84rem',
                            }, children: subtitleLine })) : null] })] }) }));
}
/** Grouped hover panel: accordion row expands description; “Open permits” uses theme Button + RouterLink. */
export function AgencyGroupAccordionList({ items, locale, shell, theme, onAfterNavigate, }) {
    const [expandedKey, setExpandedKey] = useState(false);
    return (_jsx(_Fragment, { children: items.map((sub) => {
            const hrefSub = buildDepartmentAgencyHref(sub);
            const rowLabel = translateLandingAgencyDisplayName(sub, locale);
            const subSubtitle = (sub.subtitle ?? '').trim();
            const descriptionLine = (sub.description ?? '').trim();
            const tidy = tidyAgencyDescription(descriptionLine);
            const primaryBody = tidy ||
                (!tidy && descriptionLine ? descriptionLine : '') ||
                subSubtitle ||
                shell.heroAgencyHoverFallback;
            const rowKey = sub.rowId ?? sub.recordId ?? `${sub.name}-${sub.departmentKey ?? ''}`;
            return (_jsxs(Accordion, { disableGutters: true, elevation: 0, expanded: expandedKey === rowKey, onChange: (_, isExp) => setExpandedKey(isExp ? rowKey : false), sx: {
                    '&:before': { display: 'none' },
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.95)}`,
                    '&:last-of-type': { borderBottom: 'none' },
                    bgcolor: 'transparent',
                }, children: [_jsxs(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, { sx: { color: 'primary.main', fontSize: '1.35rem' }, "aria-label": "Show description" }), sx: {
                            px: 1.75,
                            minHeight: 52,
                            '& .MuiAccordionSummary-content': {
                                my: 1,
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            },
                        }, children: [_jsxs(Box, { sx: { minWidth: 0, flex: '1 1 auto', textAlign: 'left' }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 700, lineHeight: 1.35, color: 'text.primary' }, children: rowLabel }), subSubtitle ? (_jsx(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mt: 0.35, fontSize: '0.8rem', lineHeight: 1.35 }, children: subSubtitle })) : null] }), hrefSub ? (_jsx(Button, { component: RouterLink, to: hrefSub, size: "small", variant: "outlined", color: "primary", onClick: (e) => {
                                    e.stopPropagation();
                                    onAfterNavigate();
                                }, sx: {
                                    flexShrink: 0,
                                    alignSelf: 'center',
                                    py: 0.35,
                                    px: 1.25,
                                    minWidth: 0,
                                    fontSize: '0.86rem',
                                    lineHeight: 1.2,
                                    borderWidth: 1.5,
                                }, children: shell.heroAgencyGroupedOpenPermits })) : null] }), _jsx(AccordionDetails, { sx: { px: 2, pt: 0, pb: 2, bgcolor: alpha(theme.palette.grey[50], 0.88) }, children: _jsx(Typography, { variant: "body2", color: "text.primary", sx: { lineHeight: 1.62, fontSize: '0.9rem' }, children: primaryBody }) })] }, rowKey));
        }) }));
}
